"use client";

import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { cn } from "@/core/utils/func/cn";
import type React from "react";
import { formatKey, isPlainObject, isPrimitive } from "./transaction-view/utils";

interface UserViewProps {
  data: unknown;
  className?: string;
}

const MAX_DEPTH = 4;

const stringifyValue = (value: unknown): string => {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// ── internal sub-components ──────────────────────────────────────────────────

function InfoRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex min-w-0 items-start gap-3 py-3 border-b border-border/50 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="mb-0.5 break-words text-xs text-muted-foreground">{label}</p>
        <div
          className={cn(
            "break-words text-sm font-semibold text-foreground",
            mono && "font-mono"
          )}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border bg-gradient-to-r from-muted/60 to-muted px-3 py-3 sm:px-5">
        <h3 className="break-words text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
      </div>
      <div className="px-3 py-1 sm:px-5">{children}</div>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export function UserView({ data, className }: UserViewProps) {
  const { t } = useTypedTranslation();

  // Render a primitive value as a styled node
  function renderPrimitive(value: string | number | boolean): React.ReactNode {
    if (typeof value === "boolean") {
      return (
        <span
          className={cn(
            "inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full",
            value
              ? "text-success bg-success-muted"
              : "bg-muted text-muted-foreground"
          )}
        >
          {t(value ? "yes" : "no")}
        </span>
      );
    }

    if (typeof value === "number") {
      return (
        <NoTranslate as="span" className="font-mono text-foreground">
          {Number.isNaN(value) ? "—" : value.toLocaleString()}
        </NoTranslate>
      );
    }

    return <NoTranslate as="span">{String(value) || "—"}</NoTranslate>;
  }

  // Render any value as an InfoRow value node (handles null/undefined gracefully)
  function renderValueNode(value: unknown): React.ReactNode {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground italic text-xs">—</span>;
    }
    if (isPrimitive(value)) return renderPrimitive(value);
    return (
      <span className="text-muted-foreground italic text-xs">
        {stringifyValue(value)}
      </span>
    );
  }

  // Render an array: chips for primitives, numbered sub-cards for objects
  function renderArray(arr: unknown[], depth: number): React.ReactNode {
    if (arr.length === 0) {
      return (
        <span className="text-sm italic text-muted-foreground block py-2">
          {t("shareablePreviewEmptyList")}
        </span>
      );
    }

    // Primitive-only array → chips
    if (arr.every(isPrimitive)) {
      return (
        <div className="flex flex-wrap gap-1.5 py-2">
          {arr.map((item, i) => (
            <span
              key={i}
              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20"
            >
              {String(item)}
            </span>
          ))}
        </div>
      );
    }

    // Object / mixed array → numbered sub-cards
    return (
      <div className="space-y-3 py-2">
        {arr.map((item, i) => (
          <div
            key={i}
            className="rounded-lg border border-border overflow-hidden"
          >
            <div className="px-4 py-2 bg-muted/60 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {t("shareablePreviewItemLabel", { index: i + 1 })}
            </div>
            <div className="px-4 py-1">
              {depth < MAX_DEPTH ? (
                isPlainObject(item) ? (
                  renderObjectBody(item, depth + 1)
                ) : (
                  renderValueNode(item)
                )
              ) : (
                <pre className="text-xs text-muted-foreground overflow-auto max-h-32 py-2">
                  {stringifyValue(item)}
                </pre>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Render the body of a plain object: primitive fields as InfoRows, nested as SectionCards
  function renderObjectBody(
    obj: Record<string, unknown>,
    depth: number
  ): React.ReactNode {
    const entries = Object.entries(obj);

    if (entries.length === 0) {
      return (
        <span className="text-sm italic text-muted-foreground block py-2">
          {t("shareablePreviewEmptyObject")}
        </span>
      );
    }

    if (depth >= MAX_DEPTH) {
      return (
        <pre className="text-xs text-muted-foreground overflow-auto max-h-48 py-2">
          {stringifyValue(obj)}
        </pre>
      );
    }

    const simpleEntries = entries.filter(
      ([, v]) => isPrimitive(v) || v === null || v === undefined
    );
    const complexEntries = entries.filter(
      ([, v]) => !isPrimitive(v) && v !== null && v !== undefined
    );

    return (
      <div className="space-y-3">
        {/* Primitive fields as InfoRows */}
        {simpleEntries.length > 0 && (
          <div>
            {simpleEntries.map(([key, val]) => (
              <InfoRow
                key={key}
                label={formatKey(key)}
                value={renderValueNode(val)}
              />
            ))}
          </div>
        )}

        {/* Complex nested fields as SectionCards */}
        {complexEntries.map(([key, val]) => (
          <SectionCard key={key} title={formatKey(key)}>
            {Array.isArray(val)
              ? renderArray(val, depth + 1)
              : isPlainObject(val)
                ? renderObjectBody(val, depth + 1)
                : renderValueNode(val)}
          </SectionCard>
        ))}
      </div>
    );
  }

  // Top-level rendering: objects split into a "General Info" card + per-section cards
  function renderTopLevel(value: unknown): React.ReactNode {
    if (value === null || value === undefined) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm italic text-muted-foreground">
            {t("shareablePreviewNoValue")}
          </p>
        </div>
      );
    }

    if (isPrimitive(value)) {
      return (
        <div className="px-1 py-3">
          <div className="text-base font-semibold text-foreground">
            {renderPrimitive(value)}
          </div>
        </div>
      );
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm italic text-muted-foreground">
              {t("shareablePreviewEmptyList")}
            </p>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          {value.map((item, i) => (
            <SectionCard
              key={i}
              title={t("shareablePreviewItemLabel", { index: i + 1 })}
            >
              {isPlainObject(item) ? (
                renderObjectBody(item, 1)
              ) : isPrimitive(item) ? (
                renderPrimitive(item)
              ) : (
                <pre className="text-xs text-muted-foreground py-2">
                  {stringifyValue(item)}
                </pre>
              )}
            </SectionCard>
          ))}
        </div>
      );
    }

    if (isPlainObject(value)) {
      const entries = Object.entries(value);
      const simpleEntries = entries.filter(
        ([, v]) => isPrimitive(v) || v === null || v === undefined
      );
      const complexEntries = entries.filter(
        ([, v]) => !isPrimitive(v) && v !== null && v !== undefined
      );

      return (
        <div className="space-y-4">
          {/* Primitive fields grouped in a "General Info" card */}
          {simpleEntries.length > 0 && (
            <SectionCard title={t("shareablePreviewGeneralInfo")}>
              {simpleEntries.map(([key, val]) => (
                <InfoRow
                  key={key}
                  label={formatKey(key)}
                  value={renderValueNode(val)}
                />
              ))}
            </SectionCard>
          )}

          {/* Nested objects / arrays as labelled section cards */}
          {complexEntries.map(([key, val]) => (
            <SectionCard key={key} title={formatKey(key)}>
              {Array.isArray(val)
                ? renderArray(val, 1)
                : isPlainObject(val)
                  ? renderObjectBody(val, 1)
                  : renderValueNode(val)}
            </SectionCard>
          ))}
        </div>
      );
    }

    // Fallback: raw stringified content
    return (
      <pre className="text-xs text-muted-foreground overflow-auto p-3 rounded-lg border border-border bg-card">
        {stringifyValue(value)}
      </pre>
    );
  }

  return (
    <div className={cn("min-w-0 space-y-4", className)}>
      {renderTopLevel(data)}
    </div>
  );
}
